import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { purchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { users } from "@/api/users/useUsersQuery";
import { LinkAnchor } from "@/pages/test/StandardInfoBestPracticeLinkAnchor";
import DatetimeUtil from "@/utils/dateTimeUtil";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { purchaseOrderStatusColor, setPurchaseOrderStatus } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { setToLocaleString } from "@/utils/unitMark";
import styled from "@emotion/styled";
import { ActionIcon, Badge, Flex, Text, useMantineColorScheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  PurchaseOrdersGet200ResponseRowsInner,
  ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconMinus, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { LocationDetailForm } from "../location/detail/LocationDetailForm";
import { useModal } from "../modal/ModalStackManager";
import { OrderDetailForm } from "../order/form/DetailForm";
import { OrderUpdateForm } from "../order/form/UpdateForm";
import { PartnerDetailForm } from "../partner/form/DetailForm";
import customAlert from "../ui/alert/Alert";
import { UsersDetailForm } from "../users/components/form/UsersDetailForm";
import { PurchaseOrderDetailForm } from "./form/DetailForm";
import { PurchaseOrderUpdateForm } from "./form/UpdateForm";

interface PurchaseOrderTableRowProps {
  pageKey: "purchaseOrder" | "order";
  key: string | number;
  rowData: PurchaseOrdersGet200ResponseRowsInner;
  refetch?: () => void;
  checkedItemIds: Set<number>;
  onChangeCheckedItemIds: (newSet: Set<number>) => void;
  children?: (args: {
    purchaseOrderItems: AuthSignupPost201ResponseEquipmentPurchaseOrderItem[];
    isAllChecked: boolean;
    checkedItemIds: Set<number | undefined>;
    handleToggleAll: () => void;
    handleToggleItem: (id: number) => void;
  }) => React.ReactNode;
}
export const PurchaseOrderTableRow = (params: PurchaseOrderTableRowProps) => {
  const { pageKey, key, rowData, children, refetch, checkedItemIds, onChangeCheckedItemIds } =
    params;
  const [opened, { toggle }] = useDisclosure(false);
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { data: userInfo } = useQuery(users.who({}));
  const partnerType = userInfo?.data?.partner?.type;
  const { colorScheme } = useMantineColorScheme();

  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest) =>
      mutatePurchaseOrders
        .delete(params)
        .mutationFn(params as ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch && refetch();
        customAlert(
          t("message.deleteSuccess", {
            item: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
          }),
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: `${translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType)}${translateWithPartnerType(t, `${pageKey}.id`, partnerType)}`,
          }),
          t("common.deleteFail", {
            item: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
          }),
          "red",
        );
      },
    },
  );

  const { data } = useQuery({
    ...purchaseOrderItems.findByOpenPurchaseOrder({
      query: [
        {
          $and: [
            { purchaseOrderId: { $eq: rowData.id } },
            { direction: { $eq: pageKey === "purchaseOrder" ? "INBOUND" : "OUTBOUND" } },
          ],
        },
      ],
      populate: [
        "itemName",
        "quantities",
        "location",
        "user",
        "partner",
        "purchaseOrder",
        "purchaseOrderCode",
      ],
    }),
    enabled: opened,
  });

  const items = data?.data ?? [];

  const handleToggleItem = (id: number) => {
    const newSet = new Set(checkedItemIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    onChangeCheckedItemIds(newSet);
  };

  const handleToggleAll = () => {
    if (checkedItemIds.size === items.length) {
      onChangeCheckedItemIds(new Set());
    } else {
      onChangeCheckedItemIds(new Set(items.map((i) => i.id ?? 0)));
    }
  };

  const isAllChecked = items.length > 0 && items.every((item) => checkedItemIds.has(item.id ?? -1));

  return (
    <>
      <Tr key={key} theme={{ colorScheme }}>
        <td>
          <Text>
            {!opened && (
              <IconPlus
                onClick={() => {
                  toggle();
                  onChangeCheckedItemIds(new Set());
                }}
                style={{ verticalAlign: "middle", cursor: "pointer" }}
              />
            )}
            {opened && (
              <IconMinus
                onClick={() => {
                  toggle();
                  onChangeCheckedItemIds(new Set());
                }}
                style={{ verticalAlign: "middle", cursor: "pointer" }}
              />
            )}
          </Text>
        </td>
        <td>
          <Text>{rowData.id}</Text>
        </td>
        <td>
          <Badge
            variant="filled"
            color={
              purchaseOrderStatusColor[rowData.status as keyof typeof purchaseOrderStatusColor]
            }
          >
            {setPurchaseOrderStatus(rowData.status ?? "")}
          </Badge>
        </td>
        <td>
          <Text>{rowData.purchaseOrderCategoryName}</Text>
        </td>
        <td>
          <LinkAnchor
            onClick={() =>
              pageKey === "purchaseOrder"
                ? openModal(<PurchaseOrderDetailForm purchaseOrderId={rowData.id ?? 0} />, null, "")
                : openModal(
                    <OrderDetailForm purchaseOrderId={rowData.id ?? 0} />,
                    null,
                    t("common.detail", { item: t("purchaseOrder.purchaseOrder") }),
                  )
            }
          >
            {rowData.code}
          </LinkAnchor>
        </td>
        <td>{rowData.name}</td>
        <td>
          <LinkAnchor
            onClick={() =>
              openModal(<PartnerDetailForm partnerCode={rowData.partner?.code} />, null, "")
            }
          >
            {rowData.partner?.name}
          </LinkAnchor>
        </td>
        <td>
          <LinkAnchor
            onClick={() =>
              openModal(
                <LocationDetailForm locationCode={rowData.sourceLocationCode} />,
                null,
                t("common.detail", { item: t("location.location") }),
              )
            }
          >
            {pageKey === "purchaseOrder" ? rowData.sourceLocationName : rowData.targetLocationName}
          </LinkAnchor>
        </td>
        <td>
          <LinkAnchor
            onClick={() =>
              openModal(
                <UsersDetailForm UserCode={rowData.userCode} />,
                null,
                t("common.detail", { item: t("user.user") }),
              )
            }
          >
            {rowData.manager}
          </LinkAnchor>
        </td>
        <td>{setToLocaleString(rowData.numberOfPurchaseOrderItems)}</td>
        <td>
          <Flex align="center">
            {rowData.orderDate !== null ? timeUtil(rowData.orderDate as string) : ""}
          </Flex>
        </td>
        <td>
          <Flex align="center">
            {rowData.createdAt !== null ? DatetimeUtil(rowData.createdAt as string) : ""}
          </Flex>
        </td>
        <td>
          <Flex align="center">
            {rowData.deliveryDeadline !== null ? timeUtil(rowData.deliveryDeadline as string) : ""}
          </Flex>
        </td>
        <td>
          <Flex justify="center" align="center" gap="md">
            <ActionIcon
              variant="subtle"
              onClick={() =>
                openModal(
                  pageKey === "purchaseOrder" ? (
                    <PurchaseOrderUpdateForm formatterProps={rowData} />
                  ) : (
                    <OrderUpdateForm formatterProps={rowData} />
                  ),
                  null,
                  t("common.edit", {
                    item: translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType),
                  }),
                  true,
                )
              }
              color="yellow"
            >
              <IconEdit />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => deleteMutate({ purchaseOrderId: rowData.id ?? 0 })}
              color="red"
            >
              <IconTrash />
            </ActionIcon>
          </Flex>
        </td>
      </Tr>
      {opened &&
        typeof children === "function" &&
        children({
          purchaseOrderItems: items,
          isAllChecked,
          checkedItemIds,
          handleToggleAll,
          handleToggleItem,
        })}
    </>
  );
};

const Tr = styled.tr<{ right?: boolean; theme: { colorScheme: "dark" | "light" } }>`
  background-color: ${({ theme }) =>
    theme.colorScheme === "dark" ? "var(--gray-9, #111)" : "var(--gray-0, #fff)"};
`;
